import {
  PaymentPlanLineStatus,
  PaymentMode,
  LenderName,
} from "@nemuru-eng/js-api-client";
import esLocale from "date-fns/locale/es";
import React from "react";
import { ACTION_EXCEPTIONS } from "../../../utils/getActionException";
import { DefaultTermsAndConditionCa } from "../termsAndConditions/ca/defaultTermsAndConditionsCa";
import { INSURANCE_PACK } from "../../../components/modules/card/Card/config";

const ca = {
  SIGNING_IN: "Iniciant sessió...",
  AUTH_ERROR: "Error d'autenticació",
  WELCOME_BACK: "Hola de nou",
  RESUME_LOAN_PROCESS: "Continua el teu procés.",
  CONTINUE: "Continuar",

  // Seccions
  SECTION_IDENTIFICATION: "Identitat",
  DOCUMENT_ID_VERIFICATION: "Verificació $v1",
  VERIFYING_DOCUMENT: "Verificant document…",
  UPLOADING_DOCUMENT_PICTURE: "Pujant imatge…",
  UPLOADING_FRONT_PICTURE: "Pujant imatge frontal…",
  UPLOADING_BACK_PICTURE: "Pujant imatge posterior…",
  LOADING_VERIFICATION_STATUS: "Carregant l'estat de la verificació…",
  DOCUMENT_TYPE_SELECTION_INSTRUCTIONS:
    "Verificarem la teva identitat de forma ràpida i segura. Només et portarà un parell de minuts.",
  TRUSTED_ID_VERIFICATION:
    "Les teves dades estan protegides d'acord amb el RGPD",
  START_VERIFICATION: "Iniciar verificació",
  SELECT_YOUR_DOCUMENT_TYPE: "Selecciona el teu tipus de document",
  INSTRUCTIONS_FOR_DOCUMENT_VERIFICATION: "Instruccions",
  ACTION_REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS_REJECTED: (
    <div>
      <p>
        En aquests moments no hem pogut recuperar l'estat de la verificació del
        teu document d'identitat.
      </p>
      <br></br>
      <span>Si us plau: </span>
      <p>
        <ul>
          <li>Torna-ho a intentar clicant el botó de sota.</li>
          <li>Si l'error persisteix, torna-ho a intentar més tard.</li>
        </ul>
      </p>
    </div>
  ),
  VERIFICATION_FAILED: "La verificació ha fallat",
  ID_DOC_VERIFICATION_FAILED_TITLE:
    "Ho sentim, no hem pogut verificar el teu document.",
  ID_DOC_VERIFICATION_ATTEMPTS: "Intents: $v1/$v2",
  RESTART_PROCESS: "Reiniciar procés",
  ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_TITLE: "Intents limitats",
  ID_DOC_VERIFICATION_LIMITED_ATTEMPTS_BODY:
    "Tens un nombre limitat d'intents ($v1 de $v2).",
  ID_DOC_VERIFICATION_REASON_TIMEOUT:
    "La sessió de pujada ha caducat. Si us plau, tingues el document preparat i completa el procés de fotos més ràpid en el següent intent.",
  ID_DOC_VERIFICATION_REASON_RECOGNITION:
    "No s'ha pogut reconèixer el document, pot estar danyat, pot no haver-se pujat amb l'orientació correcta o pot no correspondre al número de document d'identitat que s'ha utilitzat per iniciar aquesta sol·licitud. Utilitza el document original, assegura't que està ben enquadrat, és llegible i no té reflexos, i mantén-lo en posició horitzontal.",
  ID_DOC_VERIFICATION_MAX_ATTEMPTS:
    "Has esgotat tots els teus intents de verificació d'identitat. Si us plau, reinicia el procés i torna-ho a provar.",
  ID_DOC_UPLOAD_PROCESS_EXPIRED_TITLE: "Sessió caducada",
  ID_DOC_UPLOAD_PROCESS_EXPIRED_BODY:
    "La sessió de pujada ha caducat. Reinicia el procés per continuar.",
  ID_DOC_UPLOAD_INVALID_PHOTO_TITLE: "Foto $v1 no vàlida",
  front: "frontal",
  back: "posterior",
  ID_DOC_UPLOAD_INVALID_PHOTO_BODY:
    "No hem pogut validar aquesta imatge. Torna a fer la foto i assegura't que el document està clar i ben enquadrat.",
  ID_DOC_UPLOAD_TRANSACTIONAL_ERROR_TITLE: "No hem pogut pujar la foto",
  ID_DOC_UPLOAD_TRANSACTIONAL_ERROR_BODY:
    "Torna-ho a provar d'aquí a uns segons. Si el problema persisteix, reinicia el procés.",
  ID_DOC_UPLOAD_GENERIC_ERROR_TITLE: "Alguna cosa ha fallat",
  ID_DOC_UPLOAD_GENERIC_ERROR_BODY:
    "No hem pogut pujar les imatges. Si us plau, torna-ho a provar.",
  ID_DOC_UPLOAD_CAPTURED_GENERIC_ERROR_BODY:
    "No hem pogut pujar la imatge capturada. Si us plau, torna-ho a provar.",
  ID_DOC_VERIFY_GENERIC_ERROR_BODY:
    "No hem pogut validar el teu document. Si us plau, torna-ho a provar.",
  ID_VERIFICATION_EXTRACTED_DATA_FORM_TITLE: "Verificació d'identitat",
  ID_VERIFICATION_EXTRACTED_DATA_FORM_SUBTITLE:
    "Revisa i completa les dades del teu document d'identitat.",
  ID_VERIFICATION_EXTRACTED_DATA_LOADING: "Llegint les dades del teu document…",
  ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR_TITLE:
    "No hem pogut llegir les teves dades",
  ID_VERIFICATION_EXTRACTED_DATA_LOAD_ERROR:
    "No hem pogut llegir les dades del teu document. Si us plau, torna-ho a intentar.",
  ISSUING_COUNTRY: "País emissor",
  PROCESS_INFO_BULLET_ORIGINAL:
    "Utilitza un document original. No utilitzis una còpia.",
  PROCESS_INFO_BULLET_FRAME:
    "Assegura't que el document està enquadrat dins del marc. Ha de ser llegible i estar enfocat.",
  PROCESS_INFO_BULLET_HORIZONTAL:
    "El document ha d'estar en posició horitzontal.",
  PROCESS_INFO_BULLET_NOT_EXPIRED: "El document no ha d'estar caducat.",
  PROCESS_INFO_BULLET_NOT_UNDERAGED:
    "El titular del document ha de ser major d'edat.",
  PROCESS_INFO_BULLET_GOOD_LIGHTING:
    "Busca bona il·luminació i evita reflexos.",
  PHOTO_WILL_BE_UPLOADED_FOR_VERIFICATION:
    "Aquesta serà la foto que pujarem al sistema per a la verificació. Assegura't que el document està enquadrat dins del marc, és llegible i està enfocat.",
  PHOTO_ID_DOCUMENT_front: (
    <>
      Fes la foto <strong>davantera</strong> del teu document d'identitat
    </>
  ),
  PHOTO_ID_DOCUMENT_back: (
    <>
      Fes la foto <strong>posterior</strong> del teu document d'identitat
    </>
  ),
  STEP_X_OF_Y: "Pas $v1 de $v2",
  SECTION_FINANCES: "Finances",
  SECTION_SUMMARY: "Sol·licitud",
  SECTION_DOCUMENTATION_AND_SIGNATURE: "Documentació i signatura",
  SECTION_DOCUMENTATION: "Documentació",
  SECTION_SIGNATURE: "Signatura",

  // NotFound
  SORRY: "Ho sentim",
  PAGE_NOT_FOUND: "Pàgina no trobada",
  PROCESS_NOT_ALLOWED_FOR_THIS_LOAN:
    "Aquest procés no està permès per a aquesta sol·licitud",

  AUTOCOMPLETE: "Autocompletar",

  // Disabled
  OOPS: "¡Oh!",
  LOAN_IS_DISABLED: "La sol·licitud ja no existeix",

  // Critical Error Information
  OOPS_SOMETHING_WENT_WRONG: "¡Oh! Alguna cosa ha fallat",
  PLEASE_RESET_THE_APP:
    "Prova de reiniciar l'aplicació. Si l'error persisteix, posa't en contacte amb el nostre servei d'atenció al client.",
  COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER:
    "No hem sigut capaços de trobar el teu número de compte. Si us plau, posa't en contacte amb el nostre servei d'atenció al client al telèfon $v1.",
  COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER_CONTACT_CUSTOMER_SUPPORT_CHAT:
    "No hem sigut capaços de trobar el teu número de compte. Si us plau, posa't en contacte amb el nostre servei d'atenció al client al telèfon $v1 o per xat.",
  // InfoPanel
  WHICH_DOCUMENTS: "Quins documents has de tenir a mà?",
  WELCOME_TO_ID_VERIFICATION_TITLE: "Verifiquem la teva identitat",
  WELCOME_TO_ID_VERIFICATION_BODY:
    "Verificarem la teva identitat amb el teu document d’identificació. És un pas obligatori per poder continuar amb la teva sol·licitud.",
  WELCOME_TO_ID_VERIFICATION_REASSURANCE:
    "Normalment triga 1–2 minuts i les teves fotos s’utilitzen només per a la verificació.",
  DNI_OR_NIE: "DNI o NIE vàlid.",
  NIE_EU_PASSPORT:
    "En cas de tenir NIE Europeu sense foto, a més et demanarem el passaport.",
  WHAT_DATA_REQUESTED: "Quins altres dades et demanarem?",
  ENTRANCE_DATE: "Data d'entrada al teu domicili actual.",
  OWNERSHIP_CONTRACT:
    "Si el domicili és teu, si és contracte privat, escriptura pública o habitatge social.",
  MORTAGE_EXPENSES: "Si tens hipoteca, quant pagues d'hipoteca al mes.",
  MARITAL_REGIME_TYPES:
    "Si estàs casat/ada, si el règim és de separació de béns o de guanys.",
  IBAN: "Número de compte bancari (IBAN) on domiciliar el préstec.",
  WHAT_IS_PUBLIC_DEED: "Què és escriptura pública?",

  PUBLIC_DEED_EXPLANATION:
    "Quan el contracte de compravenda entre la part compradora i venedora, que recull per escrit les dades de totes dues parts i les condicions de l'operació (immoble, forma de pagament, etc.), s'eleva davant notari i és aquest l'encarregat de certificar la validesa del contracte i inscriure la propietat al Registre de la Propietat. Aquest és el procediment més habitual.",
  WHAT_IS_PRIVATE_CONTRACT: "Què és un contracte privat?",
  PRIVATE_CONTRACT_EXPLANATION:
    "Quan el contracte de compravenda entre la part compradora i venedora, que recull per escrit les dades de totes dues parts i les condicions de l'operació (immoble, forma de pagament, etc.), no es celebra davant un notari per donar-li la forma d'escriptura pública. El contracte privat no té accés al Registre de la Propietat.",
  WHAT_IS_SOCIAL_HOUSING: "Què és habitatge social?",
  SOCIAL_HOUSING_EXPLANATION:
    "Quan s'adquireix un immoble de l'Agència de l'Habitatge Social.",
  WHAT_IS_MATRIMONIAL_ASSETS: "Què és el règim de guanys?",
  MATRIMONIAL_ASSETS_EXPLANATION:
    "El règim de guanys posa en comú els béns i guanys obtinguts durant el matrimoni. En altres paraules, tot el patrimoni que s'obtingui durant el matrimoni passarà a ser de tots dos a parts iguals, sense importar si és fruit del treball o de les rendes de qualsevol d'ells",
  WHAT_IS_SEPARATE_PROPERTY: "Què és la separació de béns?",
  SEPARATE_PROPERTY_EXPLANATION:
    "En el règim de separació de béns, tant els béns que cadascuna de les persones té abans de casar-se com els que adquireixi després, seran seus i no de l'altra persona.",
  HOW_IT_WORKS: "Com funciona?",
  HOW_IT_WORKS_INTRO:
    "Aquest procés es realitza a través d'Eurobits Technologies (empresa que compleix amb els màxims estàndards de seguretat certificada en ISO 27001, regulada i aprovada pel Banc d'Espanya sota normativa PSD2 i Decret-Llei 19/2018). Només has de:",
  HOW_IT_WORKS_BANK: "Seleccionar el teu banc.",
  HOW_IT_WORKS_LOGIN:
    "Fer login com si es tractés de la teva banca online. Recorda que NO és el PIN de la teva targeta.",
  HOW_IT_WORKS_IBAN:
    "Seleccionar el compte bancari on desitges domiciliar les quotes del préstec.",
  HOW_IT_WORKS_DONE: "¡Llest!",
  WHAT_DATA_IS_READ: "Quins dades es llegiran?",
  WHAT_DATA_IS_READ_IDENTIFICATION: "Dades identificatives (titulars i IBAN)",
  WHAT_DATA_IS_READ_STATEMENTS: "Historial de moviments",
  WHAT_DATA_IS_READ_BALANCE: "Saldo",
  WHICH_GOAL: "Per a què es faran servir les dades?",
  WHICH_GOAL_INCOME:
    "Fem servir les dades del teu compte bancari per estimar la teva capacitat de pagament i poder avaluar la teva sol·licitud de forma ràpida i segura. A més, validem que ets el titular del compte seleccionat.",
  WHICH_GOAL_IBAN:
    "Establir el compte en el qual desitges domiciliar les quotes del préstec.",
  DNI_ES_TITLE: "DNI Espanyol",
  DNI_ES_EXPLANATION:
    "Document Nacional d'Identitat per a ciutadans/es amb nacionalitat espanyola",
  NIE_UE_TITLE: "NIE dins Unió Europea (sense foto)",
  NIE_UE_EXPLANATION:
    "Número d'Identitat d' Estranger (NIE) de ciutadans/es comunitaris/es (dins de la Unió Europea). El document no té fotografia.",
  NIE_NON_UE_TITLE: "NIE fora Unió Europea (amb foto)",
  NIE_NON_UE_EXPLANATION:
    "Correspon a la Targeta d'Identitat d'Estranger (TIE) de ciutadans/es NO europeus/es. El document conté fotografia.",

  //Welcome Wizard
  LETS_GO: "Hola de nou",
  YOUR_LOAN_PROCESS: "Així es troba el procés",
  IDENTIFICATION: "Identificació",
  ID_CHECK: "Comprovació d'identitat",
  PROFESSIONAL_AND_FINANCIAL_DATA: "Dades financeres i professionals",
  SUMMARY: "Resum",
  LOAN_ADJUSTMENT_AND_EVALUATION: "Ajust i avaluació final",
  PROPOSAL_DETAILS: "Proposta enviada per $v1 per un import de $v2€",
  PROPOSAL_DETAILS_WITHOUT_AMOUNT: "Proposta enviada per $v1",
  DOCUMENTATION_AND_SIGNATURE: "Documentació i signatura",
  SIGNATURE: "Signatura",
  UPLOAD_DOCUMENTATION_AND_SIGN: "Puja documentació i signa digitalment",

  // Slider
  HI: "Hola",
  START: "Començar",
  SKIP: "Saltar",
  DESCRIPTION: "Descripció",
  // Step 1
  WELCOME: "Benvingut/benvinguda",
  WELCOME_TO_NEMURU: "Benvingut/benvinguda a Nemuru",
  INVITATION_MSG:
    "$v1 t'ha convidat a realitzar el procés per poder pagar a plaços",
  NEUTRAL_INVITATION_MSG:
    "Se t'ha convidat a realitzar el procés per poder pagar a plaços",
  INVITATION_MSG_CARD:
    "$v1 t'ha convidat a realitzar el procés de contractació de la teva targeta de crèdit",
  GET_YOUR_LOAN: "Des d'aquí podràs sol·licitar el teu préstec.",
  // Step 2
  WHAT_IS_NEMURU: "Què és Nemuru?",
  YOU_SHOULD_KNOW: "Has de saber que...",
  NEMURU_INTRO:
    "Nemuru és la plataforma tecnològica que et permet sol·licitar la teva finançament en 3 senzills passos.",
  NEMURU_INTRO_SUB:
    "No som una financera, sinó que treballem amb les principals entitats financeres.",
  // Step 3
  WHAT_WILL_YOU_NEED: "Què necessitaràs?",
  DOCUMENT_RECOMMENDATION:
    "Abans de començar, si us plau, considera els següents punts:",
  DNI_OR_NIE_DOC: "Document DNI o NIE vàlid.",
  WE_WILL_REQUEST_INFORMATION: "et demanarem informació",
  ABOUT_YOUR_FINANCES:
    "sobre finances i dades professionals necessàries per processar la teva sol·licitud de préstec",
  ALL_INFO_PRIVATE: "Tota la informació serà privada i protegida estrictament",
  SPANISH_ACCOUNT: "Compte bancari espanyol.",
  INCOME: "Ingressos",
  SPANISH_ACCOUNT_DETAIL:
    "Has de tenir un compte bancari espanyol on domiciliar les quotes del préstec.",
  OTHER_DOCUMENTS: "Altres documents",
  OTHER_DOCUMENTS_DETAIL:
    "Si encara no ets client de CaixaBank, et demanarem que ens faciliteu un justificant d'ingressos (com una nòmina, declaració de la renda, etc.) i un justificant de titularitat del compte bancari que ens has donat per domiciliar les teves quotes.",
  //Step 5
  DNI_OR_NIE_VALID: "DNI, NIE o Passaport vàlid",
  DNI_NIE_OR_PASSPORT_DOC:
    "Si no tens un DNI o NIE Europeu vàlid, et demanarem una foto del passaport.",
  EUROPEAN_BANK_ACCOUNT: "Compte bancari europeu",
  SEPA_PAYMENT_DETAILS:
    "Has de tenir un compte bancari dins de la Zona Única de Pagaments en Euros (SEPA) on domiciliar les quotes del préstec. Si no disposes d'un compte bancari europeu, t'oferirem un mètode de pagament alternatiu a través de Flywire.",
  SEPA_BANK_ACCOUNT_TITLE: "Zona Única de Pagaments en Euros (SEPA)",
  SEPA_BANK_ACCOUNT_EXPLANATION:
    "L'àrea SEPA està formada per trenta-sis països: els membres de l'Espai Econòmic Europeu (compost pels vint-i-set estats de la Unió Europea més Liechtenstein, Islàndia i Noruega) i Andorra, Mònaco, San Marino, Suïssa, el Regne Unit i Ciutat del Vaticà.",
  // GDPR
  TERMS_AND_CONDITIONS: DefaultTermsAndConditionCa,
  ACCEPT: "Acceptar",
  CANCEL: "Cancel·lar",
  PLEASE_ACCEPT_CONDITIONS: "És necessari acceptar aquesta condició.",
  REQUIRED: "(Requerit)",
  PROVIDE_CONDITIONS_TO_CLIENT:
    "Si us plau, faciliti els termes i condicions al seu client.",
  UPF_GDPR_AND_PRIVACY_POLICY_OPTIN: (
    <p>
      Nemuru cedirà les teves dades i l'informe de solvència a la Fundació
      Institut d'Educació Continua, qui tractarà les dades d'acord amb els
      termes i condicions establertes a les seves condicions generals i a la
      seva política de privacitat que has acceptat prèviament i que pots trobar
      en el següent{" "}
      <a
        href="https://www.bsm.upf.edu/documents/condicions-generals-cat-20231106.pdf"
        target="_blank"
      >
        enllaç
      </a>
      .
    </p>
  ),

  // FLOW MESSAGES
  END_SECTION_IDENTIFICATION: "Identitat confirmada",
  END_SECTION_FINANCES: "Dades financeres introduïdes correctament",
  CONGRATULATIONS: "Felicitats $v1!",
  THANKS: "Gràcies, $v1!",
  CONGRATULATIONS_SIGNATURE_NOT_AVAILABLE: "Felicitats $v1!",
  CONGRATULATION_DOCUMENTS_UPLOADED: "Felicitats $v1!",
  CONGRATULATIONS_APPROVED_END: "Felicitats $v1!",
  WE_ARE_SORRY: "Ho sentim $v1",
  THANKS_LOAN_FORM_COMPLETED: "Has completat el procés correctament.",
  THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT:
    "La teva sol·licitud ha sigut preaprovada i està pendent de validació!",
  THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED:
    "La teva sol·licitud ha sigut preaprovada i està pendent de validació!",
  APPLICATION_PENDING_APPROVAL: "La teva sol·licitud està pendent d'aprovació!",
  LENDER_SELECTED_PENDING_APPROVAL:
    "La teva sol·licitud està pendent d'aprovació per la entitat financera seleccionada. Rebràs un correu electrònic per continuar amb el procés en cas d'aprovació.",
  THANKS_DOCUMENTATION_UPLOADED:
    "Has pujat correctament la documentació, moltes gràcies.",
  LOAN_PREAPPROVED_DOCUMENTATION_NEEDED:
    "La sol·licitud està en procés de validació per part de l'entitat financera. Puja ara la documentació per obtenir la confirmació final més ràpidament!",
  LOAN_HAS_BEEN_DENIED: "La teva sol·licitud no ha sigut aprovada",
  YOUR_COMMERCIAL_AGENT_WILL_CONTACT_YOU_SOON_FOR_MORE_DETAILS: (
    <p>
      Hem realitzat l'estudi de sol·licitud de finançament. El teu{" "}
      <strong>agent comercial</strong> et contactarà aviat per informar-te dels
      pròxims passos.
    </p>
  ),
  LOAN_ONBOARDINGS_ARE_NOT_COMPLETED: "$v1 té pendent omplir les seves dades:",
  LOAN_ANALYSING:
    "Hem llançat la teva sol·licitud a les entitats financeres i et contestarem aviat. Estigues atent!",
  LOAN_ANALYSING_DOCUMENTATION_NEEDED:
    "Hem llançat la teva sol·licitud a les entitats financeres. Puja ara la documentació per obtenir la resposta més ràpidament!",
  DOWN_PAYMENT_PENDING: "Paga l'entrada",
  INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE:
    "La teva entrada està pendent de pagament",
  YOU_MUST_PAY_THE_DOWN_PAYMENT_BEFORE_SIGNING_THE_CONTRACT:
    "Completa el pagament de l'entrada amb el mètode de pagament que més et convingui.",
  CLOSE_THE_DIALOG_AND_PAY_THE_DOWN_PAYMENT: (
    <>
      <>
        Has de realitzar el pagament de l'entrada abans de signar el contracte.
      </>
      <p>
        Tanca aquesta finestra emergent i procedeix al pagament de l'entrada,
        triant aquesta opció a la pàgina de mètodes de pagament.
      </p>
    </>
  ),

  APPROVED: "La teva sol·licitud ha sigut preaprovada!",
  IN_CASE_OF_APPROVAL: "En cas d'aprovació",
  WAITING_FOR_REMAINING_ACTIONS:
    "Un moment, estem comprovant si queda alguna acció pendent...",
  TWO_ACTIONS_REMAINING: "Completa els següents 2 passos",
  THREE_ACTIONS_REMAINING: "Completa els següents 3 passos",
  ONE_ACTION_REMAINING: "Completa el següent pas",
  NO_ACTION_REMAINING: "Tots els passos completats",
  DOCS_COMPLETED: "Documentació pujada",
  DOCS_PENDING: "Puja la teva documentació",
  ONBOARDING_COMPLETED: "Dades correctament omplertes",
  PENDING_ONBOARDING: "Pendent d'omplir dades",
  PENDING_ONBOARDING_INFO:
    "Una vegada que tots els participants de la sol·licitud hagin completat les seves dades, podràs carregar la documentació i signar el contracte de forma digital.",
  DOCS_PENDING_INFO: "Puja els documents que et demanarem a continuació.",
  DOCS_VALIDATION_PENDING: "Validar documentació",
  DOCS_VALIDATION_PENDING_INFO:
    "Dirigeix-te al taulell de finançament i valida la teva documentació ensenyant el QR que t'hem enviat per email.",
  SIGNATURE_COMPLETED: "Contracte signat",
  SIGNATURE_PENDING: "Signar el contracte",
  SIGNATURE_PENDING_INFO: "Revisa el contracte i signa'l digitalment.",
  SIGNATURE_PENDING_BY_MAIL_INFO:
    "Rebràs el contracte per signar-lo digitalment.",
  SIGNATURE_PENDING_BY_MAIL_INFO_MESSAGE:
    "Revisa el teu correu electrònic i firma des de qualsevol dispositiu.",
  SIGNATURE_PENDING_BY_APP_INFO:
    "Podràs signar el contracte en el següent pas.",
  SIGNATURE_MODULE_YET_NOT_AVAILABLE: (
    <>
      <p>El procés de signatura digital encara no està disponible.</p>
      <p>
        T'informarem per correu electrònic tan aviat com sigui possible signar
        el contracte de forma digital.
      </p>
    </>
  ),
  YOU_CAN_SAFELY_CLOSE_THIS_WINDOW: (
    <p>
      <b>Aviat rebràs una resposta al teu correu electrònic.</b>
      <br /> Ja pots tancar aquesta finestra amb seguretat.
    </p>
  ),

  RECEIVE_CONTRACT_TO_SIGN:
    "Rebràs el contracte per signar-lo digitalment des de qualsevol dispositiu.",
  INFORMATION_IN_CASE_OF_APPROVAL:
    "En cas que l'entitat financera necessiti més documentació addicional, t'informarem",
  SEND_DOCUMENTATION_NEEDED:
    "Necessites aportar la documentació requerida. Fes-ho ara!",
  UPLOAD_DOCS_NOW: "Pujar documentació ara",
  RETURN_TO_MERCHANT: "Tornar al comerç",
  REDIRECTING_TO_MERCHANT: "Redirigint al comerç",
  SIGN_NOW: "Procedir a signatura",
  SYSTEM_FAILURE_DETECTION:
    "El teu perfil no s'adapta a les polítiques de risc de les entitats bancàries col·laboradores.",
  DOCUMENTATION_UPLOADED_SUBTITLE:
    "Has pujat tota la documentació correctament.",
  DOCUMENTATION_UPLOADED_DETAIL:
    "Estigues atent! T'informarem per correu electrònic.",
  PLEASE_WAIT: "Si us plau, espera un moment",
  INFO_SYNC:
    "Si us plau, espera mentre actualitzem l'estat. Aquest procés pot trigar uns segons.",
  // SummaryView
  LETS_START: "Comencem",
  ID: "Identificació",
  ID_VERIFICATION: "Verificació d'identitat",
  FINANCES: "Finances",
  FINANCE_PROFESSIONAL_INFO: "Dades financeres i professionals",
  LOAN_ADJUSTMENT: "Ajustament i avaluació del teu préstec",
  THIS_IS_THE_PROCESS: "Així serà el procés de sol·licitud per al teu préstec",
  CHECK_ID: "Comprovar identitat",
  CHECK_ID_AGAIN:
    "Necessitem tornar a fer la comprovació d'identitat automàtica",
  RESUME_PROCESS:
    "Pots reprendre el procés de sol·licitud des d'on l'has deixat",
  RESUME: "Continuar",
  RETRY: "Tornar a intentar",
  FROM: "Des de",
  FROM_LOWERCASE: "des de",

  // KycAutomatic
  VIDEO_ID: "Identificació per vídeo",
  SELECT_DOCUMENT_TYPE: "Selecciona el tipus de document",
  REMEMBER_TO_ACTIVATE_PERMISSIONS:
    "Recorda activar els permisos de càmera i micròfon en el teu dispositiu. Tingues a mà el document d'identitat en un entorn ben il·luminat.",
  START_ID: "Iniciar identificació",
  FOLLOW_INSTRUCTIONS: "Segueix les instruccions",
  CONSTENT_VIDEO_ID:
    "Accepto que es faci la video-identificació i es recullin dades biomètriques i indicatives amb la finalitat de certificar la meva identitat. Per a més informació veure",
  MANUAL_IDENTIFICATION: "Identificació manual",
  NO_ELECTRONIC_ID_FOR_PASSPORT_MESSAGE:
    "A continuació, hauràs d'introduir la teva informació personal i pujar una foto de la pàgina bio del passaport.",
  WELL_DONE: "¡Molt bé!",
  YOUR_VIDEO_ID_HAS_BEEN_SUCCESSFULLY_RECORDED:
    "Has completat amb èxit el procés de vídeo identificació",
  YOU_CAN_SAFELY_CLOSE_THIS_TAB:
    "Ja pots tancar aquesta pestanya amb seguretat.",
  AN_ERROR_OCURRED_DURING_THE_VIDEO_ID:
    "Ha ocorregut un error durant el procés de registre de la vídeo identificació",
  PLEASE_TRY_AGAIN: "Si us plau, intenta-ho de nou fent clic al botó de sota",
  REPEAT_VIDEO_ID: "Repetir identificació de vídeo",
  SUNDAY: "diumenge",
  FRIDAY: "divendres",
  VIDEO_ID_ASSISTANCE_MESSAGE:
    "Si estàs tenint dificultats amb la identificació per videotrucada, pots trucar al $v1 de dilluns a $v2 de $v3 a $v4 per que t'ajudem i et guiarem durant el procés. Lamentem les molesties i agraïm la teva paciència.",

  // ElectronicId
  ELECTRONIC_ID_ERROR_TITLE: "Ha ocorregut un problema",

  ELECTRONIC_ID_HTTPS_REQUIRED:
    "Si us plau, revisa que l'adreça (URL) des de la qual estàs accedint comença per 'https://...'",
  ELECTRONIC_ID_IMAGE_BLURRED:
    "Neteja l'objectiu de la càmera o prova des d'un altre dispositiu amb major qualitat d'imatge.",
  ELECTRONIC_ID_AUTHORIZATION_INVALID:
    "Neteja l'objectiu de la càmera o prova des d'un altre dispositiu amb major qualitat d'imatge.",
  ELECTRONIC_ID_MAX_LENGTH_EXCEEDED:
    "Intenta millorar l'entorn de gravació (lloc amb il·luminació, sense ombres, tenir el document original a mà). També pots provar des d'un altre dispositiu amb millor qualitat d'imatge.",
  ELECTRONIC_ID_DOCUMENT_TAMPERED:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_ERROR:
    "Intenta millorar l'entorn de gravació (lloc amb il·luminació, sense ombres, tenir el document original a mà). També pots provar des d'un altre dispositiu amb millor qualitat d'imatge. Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_LUMINANCE_TOO_HIGH:
    "Prova des d'un altre lloc amb una altra il·luminació i sense ombres. També pots intentar-ho des d'un altre dispositiu amb millor qualitat d'imatge.",
  ELECTRONIC_ID_LUMINANCE_TOO_LOW:
    "Sembla que no hi ha suficient llum. Si us plau, busca un lloc millor il·luminat. També pots intentar-ho des d'un altre dispositiu amb millor qualitat d'imatge.",
  ELECTRONIC_ID_USER_ABORTED: "Si us plau, intenta-ho de nou.",
  ELECTRONIC_ID_INACTIVE_USER_ABORTED: "Si us plau, intenta-ho de nou.",
  ELECTRONIC_ID_TYPE_INVALID:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_DOCUMENT_SIDES_MISMATCH:
    "Assegura't que utilitzes un únic document del qual figures com a titular.",
  ELECTRONIC_MICROPHONE_NOT_FOUND:
    "Has d'activar el permís del micròfon des dels Ajustos del teu dispositiu o des de la configuració del buscador. També pots intentar-ho des d'un altre dispositiu.",
  ELECTRONIC_ID_MAX_FPS_EXCEEDED:
    "Intenta-ho des d'un altre dispositiu/navegador. Assegura't que estàs connectat a una xarxa amb bona connexió i estàs utilitzant un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_CAMERA_INVALID_REQUIREMENTS:
    "Intenta-ho des d'un altre dispositiu amb un altre model de càmera. Intenta-ho des d'un altre dispositiu/navegador. Assegura't que estàs connectat a una xarxa amb bona connexió i estàs utilitzant un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_DOCUMENT_GLARED:
    "Intenta-ho de nou evitant els reflexos sobre el document. Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_DEVICE_NOT_SUPPORTED: "Intenta-ho des d'un altre dispositiu.",
  ELECTRONIC_ID_PERMISSIONS_REFUSED: "Prova-ho des d'un altre dispositiu.",
  ELECTRONIC_ID_FACES_MISMATCH:
    "La persona titular del document ha de ser qui porti a terme el procés d'identificació.",
  ELECTRONIC_ID_MULTIPLE_FACES:
    "Assegura't que només la persona titular del document està present durant la identificació.",
  ELECTRONIC_ID_CAMERA_RESOLUTION_TOO_LOW:
    "Prova des d'un altre dispositiu amb una resolució més gran.",
  ELECTRONIC_ID_CONNECTION_INTERRUPTED:
    "Problemes de connexió. Prova des d'un altre lloc o connectant-te a una altra xarxa.",
  ELECTRONIC_ID_CONNECTION_ERROR:
    "Prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_VIDEO_ID_REJECTED: "",
  ELECTRONIC_ID_DOCUMENT_NOT_ALLOWED:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots fer servir una fotocòpia.",
  ELECTRONIC_ID_CAMERA_NOT_FOUND:
    "Has d'activar el permís de la càmera des de les Configuracions del teu dispositiu o des de la configuració del navegador.",
  ELECTRONIC_ID_ACCESS_TOKEN_INVALID: "",
  ELECTRONIC_ID_AGENT_ABORTED: "",
  ELECTRONIC_ID_VIDEO_ID_NOT_SUPPORTED:
    "Prova des d'un altre navegador. Navegadors recomanats: Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_BARCODE_ILLEGIBLE:
    "Neteja l'objectiu de la càmera o prova des d'un altre dispositiu amb una millor qualitat d'imatge.",
  ELECTRONIC_ID_RTC_CONNECTION_NOT_AVAILABLE:
    "Prova-ho des d'un altre dispositiu/navegador. Assegura't que estàs connectat a una xarxa amb bona connexió i estàs utilitzant un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_BANDWITH_TOO_LOW:
    "Prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_HOLOGRAM_NOT_DETECTED: "",
  ELECTRONIC_ID_CONNECTION_UNSTABLE:
    "Prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_CAPTCHA_INVALID:
    "Prova des d'un altre navegador. Navegadors recomanats: Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_AUTHORIZATION_REUSED: "",
  ELECTRONIC_ID_VIDEO_ID_BAD_REQUEST:
    "Si el problema persiste, prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_RTC_CONNECTION_LOST:
    "Prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_BARCODE_NOT_DETECTED:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots usar una fotocòpia. També pots intentar-ho des d'un altre dispositiu amb millor qualitat d'imatge.",
  ELECTRONIC_ID_FACE_SCAN_DISCONTINUITY:
    "Assegura't que el procés es realitza de forma continuada pel titular del document. Recorda que has d'utilitzar el document original i en bon estat. No pots usar una fotocòpia.",
  ELECTRONIC_ID_USER_UNDERAGE:
    "La majoria d'edat és obligatòria per poder continuar amb el procés.",
  ELECTRONIC_ID_CAMERA_UNAVAILABLE:
    "Assegura't que la càmera no està sent utilitzada per una altra aplicació. Si us plau, tanca altres aplicacions que tinguis obertes en el teu dispositiu.",
  ELECTRONIC_ID_DOCUMENT_EXPIRED:
    "Has d'utilitzar un document vigent avui en dia.",
  ELECTRONIC_ID_MICROPHONE_PERMISION_DENIED:
    "Has d'activar el permís del micròfon des dels Ajustos del teu dispositiu o des de la configuració del navegador.",
  ELECTRONIC_ID_DOCUMENT_WRONG_SIDE:
    "Repeteix el procés seguint les indicacions. Recorda que primer has de mostrar la part frontal del document i després la part posterior.",
  ELECTRONIC_ID_SMILE_ID_NOT_VALIDATED:
    "Assegura't que el procés es realitza de forma continuada per la persona titular del document.",
  ELECTRONIC_ID_TYPE_UNAUTHORIZED:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots usar una fotocòpia. El titular ha de ser major d'edat i el document ha d'estar vigent.",
  ELECTRONIC_ID_CONNECTION_LOST:
    "Prova des d'un altre lloc o connectant-te a una altra xarxa. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS)).",
  ELECTRONIC_ID_SURFACE_NOT_REFLECTIVE:
    "Recorda que has d'utilitzar el document original i en bon estat. No pots usar una fotocòpia. El sistema ha de poder verificar tots els elements de seguretat del document.",
  ELECTRONIC_ID_NO_AGENT_AVAILABLE: "",
  ELECTRONIC_ID_PERMISSION_DENIED:
    "Has d'activar els permisos de micròfon i càmera des dels Ajustos del teu dispositiu o des de la configuració del navegador.",
  ELECTRONIC_ID_BROWSER_NOT_SUPPORTED:
    "Prova des d'un altre navegador. Navegadors recomanats: Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_DOCUMENT_ILLEGIBLE:
    "Neteja l'objectiu de la càmera o prova des d'un altre dispositiu. Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_USER_INACTIVE: "",
  ELECTRONIC_ID_TYPE_NOT_SUPPORTED_IN_WEB_BROWSER:
    "Prova des d'un altre navegador. Navegadors recomanats: Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_RECORDING_ERROR:
    "Si us plau, intenta-ho de nou. Recorda que has de tenir connexió a internet i estar en un lloc amb il·luminació. Utilitza un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_FACE_SPOOFING:
    "Assegura't que el procés es realitza de forma continuada pel titular del document. Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia. El sistema ha de poder verificar tots els elements de seguretat del document.",
  ELECTRONIC_ID_DOCUMENT_SIDES_MATCH_NOT_POSSIBLE:
    "Assegura't que utilitzes un únic document del qual figures com a titular. Recorda que has d'utilitzar el document original i en bon estat. No pots utilitzar una fotocòpia.",
  ELECTRONIC_ID_RTC_CONNECTION_ERROR:
    "Intenta-ho des d'un altre dispositiu/navegador. Assegura't que estàs connectat a una xarxa amb bona connexió i estàs utilitzant un navegador recomanat (Google Chrome, Mozilla Firefox, Microsoft Edge i Safari (iOS).",
  ELECTRONIC_ID_CAMERA_PERMISSION_DENIED:
    "Has d'activar el permís de la càmera des dels Ajustos del teu dispositiu o des de la configuració del navegador.",
  ELECTRONIC_ID_LIVENESS_NOT_DETECTED: "",

  // KycManual
  DNI_ES: "DNI espanyol",
  NIE_UE: "NIE dins Unió Europea (sense foto)",
  NIE_NON_UE: "NIE fora Unió Europea (amb foto)",
  PASSPORT: "Passaport",
  SELECT_YOUR_ID: "Selecciona tipus de document",
  DOCUMENT_NUMBER: "Número de document",
  INVALID_DOCUMENT_NUMBER: "Número de document invàlid",
  EXPIRATION: "Data de validesa",
  CONTRACT_EXPIRATION: "Data de fi del treball actual",
  DOCUMENT_EXPEDITOR: "País d'expedició",
  NATIONALITY: "Nacionalitat",
  ID_INFORMATION: "Informació d'identitat",
  DOCUMENT_TYPE: "Tipus de document",
  NAME_SURNAME: "Nom i cognoms",
  PERSONAL_INFORMATION: "Informació personal",
  BASIC_ID_INFORMATION: "Informació bàsica d'identitat",
  FIRST_NAME: "Nom",
  LAST_NAME: "Cognoms",
  GENDER: "Gènere",
  MALE: "Home",
  FEMALE: "Dona",
  BIRTH_DATA: "Dades de naixement",
  BIRTHDATE: "Data de naixement",
  BIRTH_CITY: "Ciutat de naixement",
  BIRTH_PROVINCE: "Província de naixement",
  BIRTH_INFO: "Lloc i data de naixement",
  TAKE_PICTURE: "Fer foto",
  PREPARING_CAMERA: "Preparant la càmera",
  ID_DOC_CAMERA_ERROR_TITLE: "Càmera no disponible",
  ID_DOC_CAMERA_ERROR_BODY:
    "No hem pogut accedir a la teva càmera. Permet els permisos de càmera a la configuració del navegador/dispositiu o tria Pujar imatges per continuar.",
  SELECT_FROM_GALLERY: "o seleccionar del carret",
  IMAGE_ID: "Imatge del document",
  DNI_ES_FRONT: "Foto frontal del DNI",
  DNI_ES_BACK: "Foto revers del DNI",
  NIE_UE_FRONT: "Foto frontal del NIE",
  NIE_UE_BACK: "Foto revers del NIE",
  NIE_NON_UE_FRONT: "Foto frontal del NIE",
  NIE_NON_UE_BACK: "Foto revers del NIE",
  PICTURE_INSTRUCTIONS:
    "Si us plau, puja una fotografia del document de prop, centrada, ben il·luminada i sense talls.",
  PICTURE_INSTRUCTIONS_MOBILE:
    "Si us plau, intenta fer la fotografia del document de prop, centrada i sense talls. Et recomanem un entorn ben il·luminat perquè les dades siguin llegibles.",
  INVALID_DATE: "Data no vàlida",
  PAST_DATE_NOT_ALLOWED: "Data passada no vàlida",
  FUTURE_DATE_NOT_ALLOWED: "Data futura no vàlida",
  UNDERAGE_NOT_ALLOWED: "Has de ser major d'edat",
  LAST_NAME_TWO_WORDS_REQUIRED: "Introdueix els dos cognoms",
  DOCUMENT_TYPE_SHORT_NIF: "DNI",
  DOCUMENT_TYPE_SHORT_NIE_EU: "NIE (UE)",
  DOCUMENT_TYPE_SHORT_NIE_NON_EU: "NIE",
  KYC_VIDEO_VERIFICATION_CONTEXT:
    "Per acreditar que ets el titular del document que has verificat anteriorment, realitzarem una breu videoidenificació.",
  UserDetailsDocumentNumberDoesNotMatchKycException:
    "El document gravat al vídeo no coincideix amb les dades del formulari. Si us plau, repeteix el procés amb el document correcte.",
  VIDEO_ID_PENDING_INFO:
    "Verifica la autenticitat del teu document d'identitat",
  // AdditionalData
  IMAGE_ID_NIE_EU: "Imatge del passaport",
  PASSPORT_FRONT: "Foto de la pàgina bio del passaport",
  PASSPORT_PICTURE_INSTRUCTIONS:
    "Si us plau, puja una fotografia o imatge de la pàgina bio del passaport de prop, centrada, ben il·luminada i sense talls.",
  PASSPORT_PICTURE_INSTRUCTIONS_MOBILE:
    "Si us plau, intenta prendre la fotografia de la pàgina bio del passaport de prop, centrada i sense talls. Et recomanem un entorn ben il·luminat perquè les dades siguin llegibles.",
  UPLOAD_PICTURE: "Pujar imatge",
  UPLOAD_PICTURES: "Pujar imatges des de dispositiu",
  UPLOAD_PICTURES_ACTION: "Pujar imatges",
  LOAD_PICTURE: "Carregar imatge",
  UPLOAD_PICTURE_FORMAT_HINT: "Només es permeten imatges JPG o PNG.",
  UPLOAD_PICTURE_FORMAT_ERROR:
    "Format no vàlid. Si us plau, puja una imatge JPG o PNG.",

  ADDITIONAL_INFORMATION: "Informació addicional",
  CURRENT_RESIDENCE_ADDRESS: "Adreça actual de residència",
  FAMILY_SITUATION: "Situació familiar",
  ADDRESS: "Adreça",
  ADDRESS_FLOOR: "Planta",
  ADDRESS_DOOR: "Porta",
  FLOOR_NUMBER: "Planta $v1",
  DOOR_NUMBER: "Porta $v1",
  ADDRESS_DIALOG_TITLE: "Adreça actual de residència",
  CONFIRM_ADDRESS: "Confirmar adreça",
  EDIT_ADDRESS: "Modificar adreça",
  ADDRESS_DIALOG_ERROR_TITLE: "Selecciona una opció",
  ADDRESS_CONTINUE_MESSAGE:
    "Escriu la teva adreça i selecciona una de les opcions disponibles al menú desplegable per poder avançar.",
  ADDRESS_DIALOG_ERROR_MESSAGE:
    "No s'ha pogut geolocalitzar la teva adreça. Si us plau, verifica que sigui correcta, editant el camp i seleccionant una opció.",
  ADDRESS_DIALOG_SPANISH_ADDRESS_REMINDER:
    "Recorda que la teva adreça ha d'estar ubicada en territori espanyol.",
  ADDRESS_DIALOG_WARNING_MESSAGE:
    "Sembla que aquesta adreça li falta un número de carrer. Si us plau, verifica que sigui correcta.",
  ADDRESS_DIALOG_SUCCESS_MESSAGE: (
    <div>
      L'adreça sembla vàlida.
      <br />
      Fes clic a <strong>Confirmar adreça</strong> si és correcta.
    </div>
  ),
  IN_SPAIN: "(carrer i número, dins d'Espanya)",
  STREET_AND_NUMBER: "(carrer i número)",
  SPAIN: "Espanya",
  ADDRESS_ERROR_MISSING_STREET_NUMBER:
    "Si us plau, introdueix el número de carrer a l'adreça.",
  ADDRESS_ERROR_MISSING_POSTAL_CODE:
    "No hem pogut detectar el codi postal. Si us plau, intenta ajustar l'adreça introduïda.",
  ADDRESS_ERROR_MISSING_GENERAL_ERROR:
    "L'adreça no sembla vàlida. Si us plau, intenta ajustar l'adreça introduïda.",

  // AUTOCOMPLETE ADDRESS
  UPDATING_FIELDS: "Actualitzant camps...",
  LOADING: "Carregant...",
  COMPLETE_ADDRESS_MANUALLY: "Completar adreça manualment",
  STREET_NAME: "Nom del carrer",
  STREET_NUMBER: "Número del carrer",
  FLOOR: "Pis",
  DOOR: "Porta",
  POSTAL_CODE: "Codi postal",
  INVALID_POSTAL_CODE: "Codi postal invàlid",
  PROVINCE: "Província",
  COUNTRY: "País",
  CITY: "Ciutat",
  REGION: "Regió",
  LATITUDE: "Latitud",
  LONGITUDE: "Longitud",
  BUILDING_LABEL: "Bloc / Escala",
  NO_OPTIONS_TO_DISPLAY: "No hi ha opcions disponibles",
  FIELD_VALUE_MUST_BE_BELOW_X_CHAR:
    "El valor del camp no pot superar els $v1 caràcters",
  FIELD_VALUE_MUST_NOT_CONTAIN_SPACES:
    "El valor del camp no pot contenir espais",
  SEARCH_RESULTS: "Resultats de l'autocompletat:",
  SELECTED_ADDRESS: "Adreça seleccionada",
  FILL_ADDRESS_VALUES: "Omplir les dades de l'adreça:",
  EDIT_ADDRESS_VALUES: "Editar les dades de l'adreça:",
  EDIT: "Editar",
  SEARCH_AGAIN: "Tornar a cercar",
  CONFIRM_DATA: "Confirmar dades",
  ADDRESS_AUTOCOMPLETE: "Autocompletar adreça",
  ADDITIONAL_ADDRESS: "Dades complementàries d'adreça",

  HOME_INFO: "Dades del domicili actual",
  MY_CURRENT_ADDRESS_IS: "El meu domicili actual és",
  OWNERSHIP: "De la meva propietat",
  RENT: "Llogat",
  LEASING: "Propietat/Lloguer empresa",
  PARTNER: "Propietat del cònjuge",
  PARENTS: "Propietat dels pares",
  USUFRUCT: "Usufructe",
  OTHER: "Altres",
  HOUSE: "Casa",
  APARTMENT: "Apartament",
  ADDRESS_START_DATE: "Data d'entrada al domicili actual",
  HOME_EXPENSES_AMOUNT_RENT: "€/mes de lloguer",
  PROPERTY_TYPE: "Tipus de propietat",
  MORTGAGE: "Amb hipoteca",
  HOME_EXPENSES_AMOUNT: "€/mes d'hipoteca",
  YOUR_PART: "(la teva part proporcional)",
  MORTGAGE_AMOUNT_GREATER_THAN_VALUE:
    "El valor de la hipoteca haurà de ser superior a $v1 €",
  RENT_AMOUNT_GREATER_THAN_VALUE:
    "El valor del lloguer haurà de ser superior a $v1 €",
  PRIVATE_CONTRACT: "Amb contracte privat",
  PUBLIC_DEED: "Amb escriptura pública",
  SOCIAL_HOUSING: "Habitatge social",
  OTHERS: "Altres",

  MARITAL_STATUS: "Estat civil",
  MARITAL_REGIME: "Règim matrimonial",
  MARRIED: "Casat/ada",
  SEPARATED: "Separat/ada",
  SINGLE: "Solter/a",
  WIDOWED: "Vidu/a",
  DIVORCED: "Divorciat/ada",
  SEPARATED_IN_FACT: "Separat/ada de fet",
  SIMILAR_SITUATION_TO_MARRIED: "Situació similar al matrimoni",
  MATRIMONIAL_ASSETS: "Amb règim de guanys",
  SEPARATE_PROPERTY: "Amb separació de béns",
  CHILD_SUPPORT_NUMBER: "Número de fills a càrrec",

  CUPS_SUPPLY_CODE: "Codi subministrament CUPS",
  CUPS_CODE: "Codi CUPS",
  WHAT_IS_CUPS: "Què és el codi CUPS?",
  HOW_TO_FIND_CUPS: "Com trobar el meu codi CUPS?",
  CUPS_CODE_EXPLANATION: (
    <div>
      <p>
        El Codi Universal de Punt de Subministrament, també conegut com a CUPS,
        és el codi que identifica cada punt de subministrament de la xarxa de
        gas natural i electricitat a Espanya.
      </p>
      <p>
        La diferència principal entre el codi CUPS de la llum i del gas natural
        resideix en que el codi de la llum comença per ES00 i el del gas natural
        comença per ES02, tot i que pot variar en funció de la distribuïdora.
      </p>
    </div>
  ),
  HOW_TO_FIND_CUPS_EXPLANATION_1: (
    <div>
      <strong>A la factura de gas o de la llum:</strong>
      <span> </span>
      El codi CUPS de la instal·lació ve en totes les factures i aquest codi no
      varia.
    </div>
  ),
  HOW_TO_FIND_CUPS_EXPLANATION_2: (
    <div>
      <strong>Trucant a la distribuïdora</strong>
      <span> </span>
      En cas de no disposar d'una factura anterior, es pot obtenir a través de
      la distribuïdora de la zona, aportant l'adreça exacta del punt de
      subministrament i les dades personals del titular.
    </div>
  ),
  INVALID_CUPS_NUMBER: "Número CUPS invàlid",
  DOCUMENT_NUMBER_VALIDATION_FAIL:
    "Les dades de la proposta enviades pel comerç no coincideixen amb la video-identificació realitzada.",
  PLEASE: "Si us plau",
  RESEND_PROPOSAL: "Parla amb el teu comerç per enviar una nova sol·licitud",
  RETRY_VIDEO_ID:
    "Realitza una nova video-identificació amb les dades aportades pel comerç",

  // Eurobits
  FINANCIAL_DATA: "Dades financeres",
  CONNECT_WITH_YOUR_BANK: "Connecta amb el teu banc",
  UPLOAD_DOCUMENTATION: "Aportar documentació",
  START_CONNECTION_WITH_YOUR_BANK: "Connectar amb el teu banc",
  GET_INFO_BANK:
    "Aconseguim la informació que necessitem connectant amb el teu banc.",

  EUROBITS_INFO:
    "Aquest procés es realitza a través de Eurobits Technologies i és totalment privat i confidencial.",
  CONNECT_BANKING_INFO:
    "Connecta amb el teu banc amb l'accés habitual a la teva banca online",
  ONLY_WHAT_WE_NEED: "Només el que necessitem",
  EUROBITS_INFO_PHRASE1: "Només s'analitza la teva capacitat financera",
  EUROBITS_INFO_PHRASE2:
    "No es realitzarà cap tipus de càrrec al teu compte bancari",
  ONLINE_BANK: "Procés exprés",
  TRADITIONAL: "Procés estàndard",
  PAPERLESS_EASY_AND_FAST: "Sense paperassa, fàcil i ràpid",
  PAPERLESS: "Sense paperassa",
  EASY_AND_FAST: " Fàcil i ràpid",
  TOTALLY_SAFE: "Totalment segur",
  SELECT_BANK_AND_LOGIN:
    "Selecciona i fes login en la teva banca online. Recorda que no és el PIN de la teva targeta.",
  NO_PASSWORD_SAVED:
    "En cap moment tenim accés ni guardem la teva contrasenya.",
  NO_PIN: "Recorda que no és el PIN de la targeta",
  TRADITIONAL_PATH:
    "Prefereixo declarar els meus ingressos per la via tradicional",
  LONGER_PROCESS:
    "Aquest procés portarà una mica més de temps i hauràs d'esperar per saber l'estat de la sol·licitud",
  DOCUMENTATION_NEEDED:
    "Hauràs de proporcionar documents que reflecteixin els ingressos declarats així com justificant de titularitat bancària",

  ERROR_RNCP_TITLE: (
    <div>Ho sentim, no hem trobat cap compte de pagament vàlid.</div>
  ),
  ERROR_RNCP_TEXT:
    "Si us plau, torneu a intentar el procés de connexió bancària assegurant-vos de proporcionar un compte de pagament vàlid.",
  ERROR_RIDN_TITLE: (
    <div>
      Ho sentim, el titular del compte proporcionat difereix del del préstec.
    </div>
  ),
  ERROR_RIDN_TEXT:
    "Si us plau, torneu a intentar el procés de connexió bancària assegurant-vos de proporcionar un compte amb el mateix titular que la persona a qui va dirigit el préstec.",
  ERROR_RECI_TITLE: (
    <div>
      Ho sentim, no és possible assegurar la titularitat d'aquest compte.
    </div>
  ),
  ERROR_RECI_TEXT:
    "Lamentablement no podem formalitzar el préstec amb les dades introduïdes. Si ho desitgeu, podeu tornar-ho a intentar amb una connexió bancària diferent.",
  ERROR_BR_DEFAULT_TITLE: "Ho sentim, ha ocorregut un error en el procés.",
  ERROR_BR_DEFAULT_TEXT:
    "Si us plau, torneu a intentar el procés de connexió bancària.",

  // CashInCashOutMethod
  SELECT_YOUR_BANK_ACCOUNT: "Sel·lecciona el compte de domiciliació",

  // Professional Data
  PROFESSIONAL_DATA: "Dades professionals",
  EMPLOYMENT_STATUS: "Situació laboral",
  PROFESSIONAL_POSITION: "Posició",
  COMPANY_INFO: "Dades de la companyia on treballes",
  COMPANY_ACTIVITY: "Activitat de la companyia",
  COMPANY_CITY: "Ciutat de la companyia",
  COMPANY_NAME: "Nom de la companyia",
  COMPANY_YEAR: "Data d'inici del treball actual",
  INDEFINITE_EMPLOYED: "Treball amb contracte indefinit",
  TEMPORARY_EMPLOYED: "Treball amb contracte temporal",
  FIXED_DISCONTINUOUS_EMPLOYED: "Treball amb contracte fix discontinu",
  CIVIL_SERVANT: "Funcionari/ària",
  SELF_EMPLOYED: "Autònom/a",
  TRAINEE: "Becari / En pràctiques",
  CASUALTY_WORKER: "Temporada/o",
  UNEMPLOYED: "Aturat/da",
  RETIRED: "Jubilat/da",
  PENSIONER: "Pensionista",
  STUDENT: "Estudiant",
  RENTIER: "Rendista",
  WIDOWS_INCOME: "Renda de viduïtat",
  OTHER_PROFESSION: "Cura del llar",
  ADMINISTRATIVE: "Administratiu/iva",
  MANAGING_DIRECTOR: "Directiu/iva",
  MANAGER: "Gerent",
  COMPANY_OWNER: "Propietari/ària / Administrador/adora empresa",
  TECHNICIANS_AND_PROFESSIONAL: "Tècnic/a o Professional",
  SHOP_ASSISTANT: "Dependent / Cambrer/a",
  COMMERCIAL_AND_SALES_PERSON: "Comercial / Vendes",
  LABOR_WORKER: "Operari / Obrer",
  ENGINEER: "Enginyer/a",
  ARCHITECT: "Arquitecte/a",
  LAWYER_OR_NOTARY_ATTORNEY: "Advocat/ada o notari/ia",
  DOCTOR_OR_DENTIST: "Professionals de la salut",
  PHARMACEUTICAL: "Farmacèutic/a",
  VETERINARY: "Veterinari/a",
  TEACHER: "Mestre/essa",
  JOURNALIST: "Periodista",
  FARMER_OR_RANCHER: "Agricultor/a o ramader/a",
  TRANSPORTER: "Transportista",
  PROFESSIONAL_ATHLETE: "Esportista professional",
  ARTIST: "Artista",
  RELIGIOUS: "Religiós/a",
  POLICE_AND_MILITARY: "Policia / Exèrcit",
  DIPLOMATIC: "Diplomàtic/a",
  CIVIL_SERVANT_A1: "Adm. públic - Funcionari A1",
  CIVIL_SERVANT_A2: "Adm. públic - Funcionari A2",
  CIVIL_SERVANT_B: "Adm. públic - Funcionari B",
  CIVIL_SERVANT_C1: "Adm. públic - Funcionari C1",
  CIVIL_SERVANT_C2: "Adm. públic - Funcionari C2",
  CIVIL_SERVANT_E: "Adm. públic - Funcionari E",
  CIVIL_SERVANT_G1: "Adm. públic - Funcionari G1",
  CIVIL_SERVANT_G2: "Adm. públic - Funcionari G2",
  CIVIL_SERVANT_G3: "Adm. públic - Funcionari G3",
  CIVIL_SERVANT_G4: "Adm. públic - Funcionari G4",
  CIVIL_SERVANT_G5: "Adm. públic - Funcionari G5",
  AGRICULTURE_LIVESTOCK_FISHERIES: "Agricultura / Ramaderia / Pesca",
  MINING_METALLURGY: "Mineria / Metal·lúrgia",
  GROCERY: "Comestibles",
  TEXTILE_MANUFACTURE: "Tèxtil",
  WOOD: "Fusta",
  GRAPHICS_ARTS_EDITION: "Art gràfic / Edició",
  CHEMISTRY: "Química",
  MECHANICAL_MACHINERY: "Maquinària mecànica",
  ENERGY_DISTRIBUTION_ELECTRICAL_SUPPLY: "Energètica",
  OTHER_UNQUALIFIED_SERVICES: "Altres serveis no qualificats",
  OTHER_QUALIFIED_SERVICES: "Altres serveis qualificats",
  BUILDING: "Construcció",
  COMMERCE: "Comerç",
  PEDDLING: "Venda ambulant",
  TRANSPORT_TERRESTRIAL_MESSAGING: "Missatgeria per transport terrestre",
  OTHER_TRANSPORTS: "Altres tipus de transportista",
  TAXI_DRIVER: "Servei de xofer / Taxi",
  TELECOMMUNICATIONS_MAIL: "Telecomunicació / Correu",
  BARS_SERVICES: "Serveis / Gastronomia",
  I_PLUS_D: "Investigació i desenvolupament",
  ADVOCACY_JOURNALISM: "Periodisme / Advocacia",
  SECURE_FINANCIAL_SERVICES: "Serveis Financers",
  FINANCIAL_SERVICES_INSURANCE: "Asseguradora",
  IT_CONSULTANT_AUDITOR: "IT / Consultoria / Auditoria",
  ARCHITECTURE_ENGINEERING: "Enginyeria / Arquitectura",
  VETERINARY_MEDICINE: "Medicina / Veterinària",
  PUBLIC_ADMINISTRATIONS: "Administració Pública",
  ARMED_FORCES: "Forces Armades",
  EDUCATION: "Educació",
  HOLIDAY_SHOWS_SHOWS: "Vacacional / Entreteniment",
  SPORTS: "Esports",
  INDIVIDUAL_NET_INCOME: "Import net exercici anterior",
  INDIVIDUAL_FINANCIAL_DATA_DATE: "Any de l'exercici anterior",
  SELF_EMPLOYED_DATA: "Dades d'autònom/a",
  NON_VALID_CIF: "CIF no vàlid",
  COMPANY_CIF: "CIF de la companyia",

  // Validate or Edit Income
  PROVIDE_MAIN_INCOME_SOURCE: "Indica la teva principal font d'ingressos",
  INCOME_AMOUNT_GREATER_THAN_VALUE:
    "El valor dels teus ingressos haurà de ser superior a $v1€",
  NET_INCOME_TYPE: "Tipus d'ingressos",
  MONTHLY_INCOME: "Ingressos nets mensuals",
  NUMBER_OF_PAYMENTS_PER_YEAR: "Nombre de pagues anuals",
  BANK_ACCOUNT_IBAN: "Compte de domiciliació",
  BANK_ACCOUNT_IBAN_WITHIN_SEPA: "Compte de domiciliació SEPA",
  BANK_ACCOUNT_USAGE_REMINDER:
    "Aquest compte bancari només s'utilitzarà per domiciliar les quotes del préstec",
  BANK_ACCOUNT_USAGE_REMINDER_FOR_CARD:
    "Aquest compte s'utilitzarà per carregar els rebuts del teu finançament",
  INVALID_IBAN_USAGE_REMINDER:
    "El número de compte introduït no és un IBAN vàlid. Si us plau, introdueix un IBAN vàlid o tria un mètode de pagament alternatiu per al pagament de les quotes.",
  BANK_ACCOUNT_USAGE_REMINDER_FOR_INTERNATIONAL_STUDENT:
    "Aquest compte bancari ha de pertànyer a una entitat dins de la zona Euro (SEPA) i només s'utilitzarà per domiciliar les quotes del préstec.",
  IBAN_NOT_IN_SEPA_LIST_USAGE_REMINDER:
    "El número de compte introduït no correspon a una entitat de la zona Euro (SEPA). Si us plau, introdueix un IBAN vàlid o tria un mètode de pagament alternatiu per al pagament de les quotes.",
  DIRECT_DEBIT_ACCOUNT: "Domiciliació",
  SALARY: "Salari",
  PENSION: "Pensió",
  UNEMPLOYMENT_BENEFIT: "Prestació d'atur",
  OTHER_PAYMENT: "Altres tipus d'ingressos",
  NO_INCOME_TITLE:
    "Ho sentim, no hem sigut capaços de detectar ingressos al compte",
  NO_INCOME_BODY:
    "No t'amoïnis, pots introduir la informació sobre els teus ingressos de forma manual.",
  NO_INCOME_BODY_DOC:
    "Hauràs de pujar la documentació que acrediti els teus ingressos al final del procés.",
  IBAN_NOT_IN_SEPA_LIST:
    "El IBAN introduït no es troba a la llista de països SEPA.",
  USE_DIFFERENT_PAYMENT_METHOD: "Utilitza un altre mètode de pagament",
  ALTERNATIVE_PAYMENT_METHOD: "Mètode de pagament alternatiu",
  INFORMATION: "Informació",
  SELECT_THIS_PAYMENT_METHOD: "Selecciona aquest mètode",
  DO_NOT_HAVE_BANK_ACCOUNT_NUMBER_WITHIN_THE_SEPA_ZONE:
    "No tinc un número de compte bancari dins de la zona SEPA",
  ALTERNATIVE_PAYMENT_METHOD_EXPLANATION: (
    <>
      <p>
        En cas de no disposar d'un número de compte (IBAN) dins de la zona SEPA,
        els pagaments es poden realitzar a través de Flywire, la plataforma que
        permet fer pagaments globals.
      </p>
      <p>
        Si escolleixes aquest mètode, abans de la data de venciment de cada
        quota, rebràs un correu electrònic amb les instruccions per completar el
        pagament de forma ràpida i segura.
      </p>
    </>
  ),
  // Adjust Loan
  GOOD: "Ben fet",
  YOUR_LOAN_RESULT: "Ja gairebé has acabat!",
  ADJUST_YOUR_QUOTA: "Ajusta la quota que vols pagar",
  LOAN_DURATION: "Durada",
  LOAN_TOTAL: "Import de la compra",
  CREDIT_LINE_TOTAL: "Import de línia de crèdit",
  TIN: "TIN",
  TAE: "TAE",
  TOTAL_TO_PAY: "Total a pagar",
  GRACE_PERIOD: "Carencia",
  SETUP_FEE: "Comissió d'obertura",
  APPLY_FOR_LOAN: "Sol·licitar",
  EURO_PER_MONTH: "$v1 €/mes",
  MONEY_PER_MONTH: "$v1/mes",
  X_MONTHS: "$v1 mesos",
  CONDITIONS_MULTI_PRICING_MESSAGE:
    "L'import de les quotes pot variar segons la entitat financera. Te mostrem la millor quota disponible com a referència, i abans de pagar podràs revisar les condicions finals.",
  APPLY_FOR_LOAN_BODY:
    "La sol·licitud només serà vinculant quan firmis el contracte en cas d'aprovació",
  DATA_TREATMENT: "Tractament de dades",
  INFORMATION_ABOUT_RISK_ANALYSIS:
    "Informació sobre el tractament de les teves dades per analitzar la teva solvència",
  AGREE_CONDITIONS: "Estic d'acord amb les condicions",
  NEED_TO_CHOOSE_TO_CONTINUE:
    "Per poder continuar és necessari escollir 'Sí' ✓ o 'No' ✗ en totes les opcions disponibles.",
  PLEASE_WAIT_APPLICATION:
    "Si us plau, espera un moment, estem processant la teva sol·licitud. Aquest procés pot durar fins a 1 minut.",
  TAKING_A_BIT_LONGER: "Ja gairebé està, si us plau, mantingues la teva espera",
  ADD_CO_BORROWER: "Afegir cotitular",
  ADD_GUARANTOR: "Afegir avalista",
  CO_BORROWER_PERSONAL_DATA: "Dades personals cotitular",
  CO_BORROWER_CONTACT_DATA: "Dades de contacte cotitular",
  CO_BORROWER_FIRST_NAME: "Nom cotitular",
  CO_BORROWER_LAST_NAME: "Cognoms cotitular",
  CO_BORROWER_DOCUMENT_NUMBER: "DNI/NIE cotitular",
  CO_BORROWER_EMAIL: "Email cotitular",
  CO_BORROWER_PHONE_NUMBER: "Mòbil cotitular",
  GUARANTOR_PERSONAL_DATA: "Dades personals avalista",
  GUARANTOR_CONTACT_DATA: "Dades de contacte avalista",
  GUARANTOR_FIRST_NAME: "Nom avalista",
  GUARANTOR_LAST_NAME: "Cognoms avalista",
  GUARANTOR_DOCUMENT_NUMBER: "DNI/NIE avalista",
  GUARANTOR_EMAIL: "Email avalista",
  GUARANTOR_PHONE_NUMBER: "Mòbil avalista",
  CO_BORROWER_ADDITION_INFO_MESSAGE_BODY:
    "Enviarem un SMS i email al cotitular amb un enllaç perquè porti a terme aquest mateix procés de sol·licitud amb la seva informació.",
  GUARANTOR_ADDITION_INFO_MESSAGE_BODY:
    "Enviarem un SMS i email a l'avalista amb un enllaç perquè porti a terme aquest mateix procés de sol·licitud amb la seva informació.",
  CO_BORROWER_RELATION: "Relació cotitular",
  GUARANTOR_RELATION: "Relació avalista",
  CO_BORROWER_GUARANTOR_ADDED_CORRECTLY:
    "Cotitular/Avalista afegit/da correctament",
  GUARANTOR_ADDED_CORRECTLY: "Avalista afegit correctament",
  borrower: "Titular",
  co_borrower: "Cotitular",
  guarantor: "Avalista",
  PARTICIPANT_ROLE: "Tipus de cotitular",
  SPOUSE: "Cònjuge",
  SIBLING: "Germana/germà",
  PARENT: "Pare/mare",
  SON: "Filla/fill",
  REST_OF_FAMILY: "Altres familiars",
  PLEASE_SELECT_DOWN_PAYMENT_AND_TERM:
    "Si us plau, selecciona entrada i termini",
  FROM_X_TO_Y_MONTHS: "De $v1 a $v2 mesos",
  FROM_X_TO_Y_YEARS: "De $v1 a $v2 anys",
  FROM_X_TO_Y: "De $v1 a $v2",
  INVALID_TERM_VALUE: "Termini no inclòs a la tarifa",
  TERM_MUST_BE_BETWEEN_X_AND_Y: "Permès entre $v1 i $v2 mesos",
  MONTHS: "Mesos",
  YEARS: "Anys",
  UP_TO: "Fins a",
  TERM: "Termini",
  DOWN_PAYMENT: "Entrada",
  VEHICLE_PRICE_WITH_TAX: "Preu vehicle (inc. impostos)",
  APPLICATION_AMOUNT: "Import a finançar",
  TOTAL_PRICE_IN_SPLIT_PAYMENT: "Total a pagar",
  FEE_AMOUNT_AND_PAYMENT_PROTECTION_INSURANCE_MONTHLY_PERCENTAGE:
    "*Inclou $v1 de comissió d'obertura finançada i assegurança de protecció de pagaments $v2 mensual.",

  INCLUDES_UPFRONT_SETUP_FEE_AMOUNT: "*Inclou $v1 de comissió d'obertura.",
  INCLUDES_FINANCED_SETUP_FEE_AMOUNT:
    "*Inclou $v1 de comissió d'obertura finançada mensualment.",
  DOWN_PAYMENT_OVER: "Entrada superior a",
  DOWN_PAYMENT_UNDER: "Entrada inferior a",
  INVALID_VALUE: "El valor no es vàlid",
  AUTO_ADJUSTED_TERM: "Termini màxim ajustat a l'antiguitat del vehicle.",
  DOWNPAYMENT_NOT_ALLOWED: "No es permet el ajust de l'entrada.",
  LOADING_LOAN_CONDITIONS: "Carregant condicions del prestec...",
  CALCULATING_CONDITIONS: "Calculant condicions...",

  // Ajuda per als camps del formulari
  HELP: "Ajuda",
  CLOSE: "Tancar",
  REQUIRED_FIELD: "Requerit",
  INVALID: "No vàlid",
  RECOMMENDED_FIELD: "Recomanat",
  PLEASE_COMPLETE_THIS_FIELD_CORRECTLY:
    "Si us plau, completa correctament el camp",
  INVALID_FORMAT: "Format invàlid",
  PLEASE_CHECK_INPUT_DATA: "Si us plau, revisa les dades introduïdes.",
  NOT_VALID_IBAN_FORMAT: "Format d'IBAN invàlid",
  INVALID_SPANISH_IBAN: "Si us plau, introdueix un IBAN espanyol.",

  // Documentation
  DOCUMENTATION: "Documentació",
  DOCUMENTATION_UPLOAD: "Si us plau, puja els següents documents",
  DOCUMENTATION_SYNC:
    "Si us plau, espera mentre actualitzem la documentació. Aquest procés pot trigar uns segons.",
  PENDING_DOCUMENTATION: "$v1 documents",
  VALIDATE_DOCUMENTATION: "Pujar documents",
  VALIDATE_DOCUMENTATION_WARNING: "Recorda fer clic a pujar documents!",
  FILES_WITH_INCIDENCE_WARNING:
    "Oops! El document no és vàlid. Si us plau, torna a afegir-lo i fes clic a Fet",
  ADD_FILE_CONFIRMATION: "Afegir nou document",
  ADD_FILE_DESCRIPTION: "Es pujarà el següent document",
  ADD_FILE_ACTION: "Afegir document",
  UPLOAD_FILE_ACTION_ASSERTION:
    "Si us plau, confirma la documentació abans de pujar-la",
  INCIDENCE: "Incidència documental",
  DONE: "Fet",
  UPLOAD_FILE_ACTION: "Pujar",
  UPLOAD_FILE_ACTION_MULTIPLE: "Pujar $v1 documents",
  UPLOAD_FILE_ACTION_SINGLE: "Pujar 1 document",
  UPLOADING_FILE_ACTION_MULTIPLE: "Pujant $v1 documents...",
  UPLOADING_FILE_ACTION_SINGLE: "Pujant 1 document...",
  BANK_ACCOUNT_OWNERSHIP_DOCUMENTATION:
    "Certificat de titularitat del compte bancari",
  DOCUMENT_REQUIRED_DESCRIPTION_BANK_ACCOUNT_OWNERSHIP_DOCUMENTATION:
    "Certificat de titularitat del compte bancari, rebut o captura de pantalla de la banca online (ha d'incloure l'IBAN, nom i DNI del titular)",
  PAYROLL: "Última nòmina disponible",
  PAYROLL_TWO_PREVIOUS_MONTHS: "Penúltima nòmina disponible",
  PERSONAL_INCOME_TAX: "IRPF anual",
  BIOMETRICS_DOCUMENT: "Document biomètric del procés KYC",
  DOCUMENT_ID_FRONT: "Part frontal del DNI / NIE",
  DOCUMENT_ID_BACK: "Part posterior del DNI / NIE",
  DOCUMENT_ID_FRONT_DNI: "Part frontal del DNI",
  DOCUMENT_ID_BACK_DNI: "Part posterior del DNI",
  DOCUMENT_ID_FRONT_NIE: "Part frontal del NIE",
  DOCUMENT_ID_BACK_NIE: "Part posterior del NIE",
  UPLOAD_DOCUMENT_ID_PICTURES: "Pujar documents d'identitat",
  LOAD_DOCUMENT_ID_FRONT_NIE: "Carregar part frontal del NIE",
  LOAD_DOCUMENT_ID_BACK_NIE: "Carregar part posterior del NIE",
  LOAD_DOCUMENT_ID_FRONT_DNI: "Carregar part frontal del DNI",
  LOAD_DOCUMENT_ID_BACK_DNI: "Carregar part posterior del DNI",
  ENROLLMENT_OF_THE_STUDENT: "Justificant de matrícula",
  DOCUMENT_REQUIRED_DESCRIPTION_ENROLLMENT_OF_THE_STUDENT:
    "Justificant de matrícula del curs",
  DATE_FORMAT_YEAR: "Any",
  DATE_FORMAT_MONTH_YEAR: "Mes, any",
  DATE_FORMAT_DAY_MONTH_YEAR: "Dia, mes, any",
  DATE_LOCALE: esLocale,
  SAVE_CHANGES_PROMT: "Vols desar els canvis fets?",
  SAVE_CHANGES: "Desar canvis",
  DISCARD_CHANGES: "Descartar canvis",
  DOCUMENT_ID_NUMBER_DIFFERENT_THAN_INVITATION:
    "El número de document no correspon a la proposta de finançament",

  //CARD
  FIXED_INSTALMENT: (
    <>
      Pagament ajornat
      <br />
      (revolving)
    </>
  ),
  END_OF_MONTH: "Fi de mes",
  FIXED_INSTALMENT_DESCRIPTION: (
    <>
      Amb la modalitat de pagament aplazat (revolving), es paga a plazos, amb la
      quota mensual que elijis, el crèdit que es va utilitzant amb el tipus
      d'interès acordat.
      <br />
      Pots elegir l'import de la quota que vols pagar mensualment, sempre a
      partir d'una quota mínima que establecem per evitar que la deuda es
      prolonga en el temps.
    </>
  ),
  END_OF_MONTH_DESCRIPTION: (
    <>
      Amb la modalitat de pagament Fi de mes, el client paga al final del mes,
      sense interessos, tot el crèdit utilitzat durant el mes. L'opció
      seleccionada no afecta a aquesta compra ni a futurs pagaments fraccionats.
      <br />
      Recorda que des de l'app pots tornar a canviar d'opció quan vulguis.
    </>
  ),
  SELECT_PAYMENT_MODE: "Tria la teva forma de pagament",
  SELECT_PAYMENT_MODE_SUBTITLE:
    "Tria com vols pagar les teves pròximes compres",
  CARD_SHIPPING_DESCRIPTION:
    "Quan acabis la sol·licitud, podràs recollir la teva targeta a l'espai habilitat a la botiga o bé te l'enviarem perquè la puguis utilitzar en les teves compres.",
  FIXED_INSTALMENT_INFORMATION_DESCRIPTION:
    "Recorda que a partir d'ara es aplica una nova fórmula amb un factor corrector per calcular la teva quota mínima i que l'import de la quota mínima pot canviar d'un mes a un altre ja que té en compte el total de l'import gastat. L'objectiu d'aquesta nova fórmula és assegurar que amb aquesta quota mínima el pagament de la deuda no es prolongui excessivament i també no pagis interessos de forma indefinida.",
  OTHER_AMOUNT: "Un altre import",
  REPAYMENT_AMOUNT_GREATER_THAN_MAX: "L'import màxim és de $v1€",
  MONTHLY_AMOUNT: "Import mensual:",
  CARD_INSURANCE: "Assaigs de protecció de pagaments",
  CARD_INSURANCE_SUBTITLE:
    "Activa el teu assaigs de protecció de pagaments de forma voluntària",
  SEE_EXAMPLE: "Veure exemple",
  WHAT_IS_THE_INSURANCE_COST: "Quin és el cost de l'assegurança?",
  CARD_INSURANCE_EXAMPLE_CAPTION_TOP:
    "El cost de l'assegurança serà de $v1 per cada 100€ de deute.",
  CARD_INSURANCE_EXAMPLE_CAPTION_BOTTOM:
    "Exemple de compra de 300€ en 3 quotes de 100€",
  CARD_INSURANCE_EXAMPLE_COL_MONTH: "Mes",
  CARD_INSURANCE_EXAMPLE_COL_DEBT: "Deute",
  CARD_INSURANCE_EXAMPLE_COL_INSURANCE: "Què pago d'assegurança?",
  CARD_INSURANCE_EXAMPLE_COL_TOTAL: "Què pago en total?",
  CARD_INSURANCE_EXAMPLE_MONTH_1: "Mes 1",
  CARD_INSURANCE_EXAMPLE_MONTH_2: "Mes 2",
  CARD_INSURANCE_EXAMPLE_MONTH_3: "Mes 3",
  CARD_INSURANCE_DESCRIPTION_START: (
    <>
      Estar protegit amb l'Assegurança de Protecció de Pagaments et suposa una
      mínima quantitat al mes i, a canvi, et beneficies de la màxima
      tranquil·litat.
    </>
  ),
  CARD_INSURANCE_DESCRIPTION_END: (
    <>
      Aquesta assegurança és voluntària i la seva no contractació no afecta les
      condicions econòmiques del producte de finançament.
    </>
  ),
  CARD_INSURANCE_CONDITIONS:
    "Només hauràs de pagar $v1 al mes per cada 100€ de deute.",
  IMPORTANT_INFORMATION: "Informació important!",
  CARD_DOCUMENTATION_VALIDATION_INSTRUCTIONS: (
    <>
      Després de la signatura del contracte, serà necessari{" "}
      <strong>validar la documentació</strong>. Dirigeix-te al taulell de
      finançament d'IKEA amb el <strong>codi QR</strong> que rebràs al teu
      correu electrònic perquè un agent faci la validació.
    </>
  ),

  UNEMPLOYMENT_PROTECTION_INSURANCE: "Protección de desempleo",
  UNEMPLOYMENT_PROTECTION_INSURANCE_DESCRIPTION:
    "Indemnització amb el 10% del saldo disponible fins a la data del despatx, i amb un màxim de 6 mensualitats conseqüents amb un màxim de 400€ al mes.",
  PAYMENT_PROTECTION_INSURANCE: "Garantia de pagament",
  APPLY_WITH_INSURANCE: "Sol·licitar amb assegurança",
  APPLY_WITHOUT_INSURANCE: "Sol·licitar sense assegurança",
  [`PAYMENT_PROTECTION_INSURANCE_DESCRIPTION_${INSURANCE_PACK.SENIOR}`]:
    "Cobertura de liquidació del crèdit pendent en cas de falleciment del titular.",
  [`PAYMENT_PROTECTION_INSURANCE_DESCRIPTION_${INSURANCE_PACK.PROTECTION_PLUS}`]:
    "Cobertura de liquidació del crèdit pendent en cas de gran invalidesa, falleciment, càncer o infart del titular.",
  CONTINUE_WITH_INSURANCE: "Continuar amb el seguro",
  CONTINUE_WITHOUT_INSURANCE: "Continuar sense el seguro",
  DO_YOU_WANT_TO_HAVE_INSURANCE: "¿Vols protegir les teves futures compres?",
  PLEASE_SELECT_IF_YOU_WANT_TO_HAVE_INSURANCE:
    "Si us plau, selecciona si vols sol·licitar el seguro",
  LEGAL_TERMS: "Informació legal",
  LEGAL_TERMS_SUBTITLE: "Sobre el seguro de protecció de pagaments",
  LEGAL_TERMS_DESCRIPTION: (
    <>
      <p>
        Al indicar "Vull protegir les meves compres", declaro, als efectes
        d’aquesta sol·licitud, que a dia d’avui no pateixo cap malaltia greu que
        jo conegui, ni em trobo en situació d’invalidesa permanent en algun dels
        seus graus (total, parcial, absoluta o gran invalidesa), o temporal que
        pugui derivar en invalidesa permanent, ni em trobo en situació d’atur,
        en cas que contracti la cobertura d’atur o incapacitat temporal.
      </p>
      <p>
        Informació subjecta a les condicions de la pòlissa contingudes en les
        Condicions generals de contractació a les quals he accedit i que he
        llegit. CaixaBank Payments & Consumer, E.F.C., E.P., S.A.U. (CaixaBank
        Payments & Consumer), amb CIF A-08980153 i domicili a l’Avinguda de
        Manoteras núm. 20, Edifici París, 28050 Madrid, media en la contractació
        de l’assegurança com a operador de bancassegurances vinculat de
        VidaCaixa S.A. d’Assegurances i Reassegurances i de SegurCaixa Adeslas
        S.A. d’Assegurances i Reassegurances, inscrit en el Registre
        administratiu especial de mediadors d’assegurances de la Direcció
        General d’Assegurances i Fons de Pensions, amb el número OV0077, segons
        pot comprovar-se davant d’aquesta, amb domicili al Passeig de la
        Castellana, 259C, edifici Torre de Cristall, Madrid (28046), telèfon 91
        339 70 00, www.dgsfp.mineco.es , té concertada una assegurança de
        responsabilitat civil i disposa d’una capacitat financera d’acord amb
        els requisits legals. L’empresa matriu de CaixaBank Payments & Consumer
        té una participació superior al 10% del capital social de VidaCaixa i de
        SegurCaixa.
      </p>
    </>
  ),
  SEE_LEGAL_TERMS: "Ver informació legal",
  VALIDATION_LINK: "Validació de dades",
  VALIDATION_LINK_SUBTITLE: "Escanea el código QR para validar tus datos",
  VALIDATION_LINK_QR_CODE_DESCRIPTION:
    "Com a últim pas del procés, has de dirigir-te a la atenció al client d'IKEA, on un agent validarà la informació i procedirà a l'emissió de la teva targeta",
  VALIDATION_LINK_QR_CODE_DESCRIPTION_1: (
    <>
      <p style={{ fontSize: "1.25rem", fontWeight: "700" }}>Felicitats!</p>
      <p>
        <strong>
          Has completat amb èxit la sol·licitud de la teva targeta VISA IKEA.
        </strong>
      </p>
      <p>
        Per facilitar-te la compra d'avui, dirigeix-te a qualsevol zona de
        planificació.{" "}
        <strong>
          Disposes <u>de fins a 3 hores per recollir la teva targeta!</u>
        </strong>
      </p>
      <p>
        Mostra el següent <strong>codi per agilitzar la gestió:</strong>
      </p>
    </>
  ),
  VALIDATION_LINK_QR_CODE_DESCRIPTION_2: (
    <>
      <p>
        <strong>
          Et recomanem acudir el més aviat possible, però si creus que no podràs
          recollir-la en les pròximes 3 hores, no et preocupis! Rebràs la teva
          targeta al teu domicili en un termini de 7 a 10 dies.
        </strong>
      </p>
      <p>
        Recorda que podràs activar i gaudir de totes les funcionalitats de la
        teva targeta en la nostra app: des d'enrolar-la fins a gestionar el
        fraccionament de les teves compres.
      </p>
    </>
  ),
  PRODUCT: "Producte",
  PAYMENT_MODE: "Modalitat de pagament",
  FIXED_INSTALMENT_AMOUNT: "Import del pagament ajornat",
  SELECT_FIXED_INSTALMENT_AMOUNT: "Selecciona un import:",
  FIXED_INSTALMENT_AMOUNT_SUBTITLE:
    "Elige la quantitat de diners que vols pagar cada mes",
  PRODUCT_DESCRIPTION: "Descripció del producte",
  [PaymentMode.END_OF_MONTH]: "Fi de mes",
  [PaymentMode.FIXED_INSTALLMENT]: "Pagament ajornat",
  CREDIT_LIMIT_NOTICE: "Límit de crèdit concedit: $v1",

  // ACTIONS MESSAGES
  ACTION_GENERIC_ERROR: "Ups! S'ha produït un error.",
  ACTION_GENERIC_INFO: "L'acció s'ha realitzat correctament",
  ACTION_GENERIC_SUCCESS: "L'acció s'ha realitzat correctament",
  ACTION_GENERIC_WARNING: "Si us plau, revisa les dades",
  ACTION_GENERIC_ERROR_TITLE: "No s'ha pogut realitzar l'acció",

  // ACTIONS MESSAGES
  ACTION_REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID_REJECTED: (
    <div>
      <p>
        En aquests moments no hem sigut capaços de connectar amb el banc per
        continuar amb el procés de firma.
      </p>
      <br></br>
      <span>Si us plau:</span>
      <p>
        <ul>
          <li>
            Torni a intentar connectar amb el banc fent clic al botó de sota.
          </li>
          <li>Si l'error persisteix, torni a intentar-ho més tard.</li>
        </ul>
      </p>
    </div>
  ),
  //Exceptions
  EXCEPTION_BorrowerOnboardingDetailsReferenceIsNotNullException:
    "Aquesta sol·licitud ja té un cotitular assignat",
  [ACTION_EXCEPTIONS.INVALID_DOCUMENT_NUMBER_EXCEPTION]: (
    <>
      <p>Sembla que el número del document d'identitat no és vàlid.</p>
      <p>Si us plau, torna a realitzar la video-identificació.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_ELECTRONIC_ID_INFORMATION_EXCEPTION]: (
    <>
      <p>Sembla que alguna informació no ha estat enregistrada correctament.</p>
      <p>Si us plau, torna a realitzar la video-identificació.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_ISO3166_EXCEPTION]: (
    <>
      <p>
        Sembla que la teva nacionalitat no ha estat correctament enregistrada.
      </p>
      <p>Si us plau, torna a realitzar la video-identificació.</p>
    </>
  ),
  [ACTION_EXCEPTIONS.INVALID_DATE_EXCEPTION]: (
    <>
      <p>Sembla que la data és invàlida.</p>
      <p>Si us plau, torna a realitzar la video-identificació.</p>
    </>
  ),
  RETRY_VIDEO_ID_BUTTON: "Reintentar video-identificació",
  // SignatureEmbeddedSAML
  SIGN: "Revisar i signar contracte",
  FOLLOW_INSTRUCTIONS_TO_SIGN: "Segueix les instruccions per signar contracte",
  PROCEED_TO_DIGITAL_SIGNATURE: "Revisió i signatura digital",
  CONTRACT_SIGNATURE: "Signatura de contracte",
  CONNECTING: "Connectant...",
  RETRIEVING_DATA: "Recuperant dades...",
  DIGITAL_SIGNATURE_INFO:
    "En continuar iniciaràs el procés de signatura digital",
  REVIEW_SIGNATURE_INFO: "No t'amoïnis, podràs revisar-ho tot abans de signar",
  SIGNATURE_URL_REQUEST_ERROR:
    "En aquests moments no hem sigut capaços de connectar amb el banc per continuar amb el procés de firma.",
  TRY_AGAIN_PRESS_BUTTON:
    "Torni a intentar connectar amb el banc clicant el botó de sota.",
  TRY_AGAIN_LATER: "Si l'error persisteix, torni a intentar-ho més tard.",
  BEFORE_PROCEEDING_YOU_NEED_TO_ANSWER:
    "Abans de signar el contracte, es requereix que respongui a les següents opcions.",
  FINALIZE: "Continuar",
  CLICK_FINALIZE_AFTER_SIGNATURE: 'Al acabar la firma, fes clic en "Continuar"',
  ACTION_LOAN_NOT_SIGNED: (
    <>
      Encara no hem pogut validar que la firma del contracte s'hagi completat.
      <br />
      <p style={{ fontWeight: "normal" }}>
        És necessari confirmar que el procés de firma s'hagi finalitzat per
        continuar amb la sol·licitud. Un cop completat el procés, torna a fer
        clic en "Continuar".
      </p>
    </>
  ),

  // Misc
  POWERED_BY: "Powered by",

  THE_IDENTIFICATION_PARAMETERS_FOR_THIS_PROCESS_ARE_NOT_CORRECT:
    "Les credencials requerides per a aquest procés no són correctes",
  PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE:
    "Documentació pujada correctament.",

  //Payment
  BACK: "Tornar",
  PAYMENT: "Pagament",
  YOUR_INSTALMENT: "La teva quota",
  INSTALMENT_DETAIL: "Detall de la quota actual",
  REVIEW_PAYMENT_PLAN: "Revisar pla de pagament",
  YOUR_PAYMENT_PLAN: "El teu pla de pagament",
  PAYMENT_PLAN_STATUS: "Estat de les quotes pagades",
  PAY_QUOTA_NUMBER: "Pagar quota $v1",
  TOTAL_PAID_AMOUNT: "Total pagat",
  TOTAL_REMAINING_AMOUNT: "Total pendent",
  INSTALMENTS_PAID: "Quotes pagades",
  OF: "de",
  PAY_AMOUNT: "Pagar $v1",
  QUOTA_NUMBER: "Número de quota",
  QUOTA_AMOUNT: "Import de la quota",
  DUE_DATE: "Data de venciment",
  PAYMENT_STATUS: "Estat del pagament",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PENDING}`]: "Pendent",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PROCESSING}`]:
    "Pagament iniciat",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.PAID}`]: "Pagada",
  [`PAYMENT_PLAN_LINE_STATUS_${PaymentPlanLineStatus.CANCELLED}`]:
    "Cancel·lada",
  PAYMENT_PLAN_LINE_PAID_TITLE: "Quota pagada",
  PAYMENT_PLAN_LINE_PAID_TEXT:
    "Aquesta quota està pagada correctament. En aquest moment, no queda cap acció pendent per realitzar. Pots tancar aquesta pestanya.",
  PAYMENT_PLAN_LINE_CANCELLED_TITLE: "Quota cancel·lada",
  PAYMENT_PLAN_LINE_CANCELLED_TEXT:
    "Aquesta quota està cancel·lada. En aquest moment, no queda cap acció pendent per realitzar. Pots tancar aquesta pestanya.",
  PAYMENT_RESULT_SUCCESS_TITLE: "Tot llest!",
  PAYMENT_RESULT_SUCCESS_TEXT:
    "El pagament de la quota s'ha realitzat correctament. Pots tancar aquesta pestanya.",
  PAYMENT_RESULT_ERROR_TITLE: "S'ha produït un problema",
  PAYMENT_RESULT_ERROR_TEXT:
    "No s'ha pogut realitzar el cobrament d'aquesta quota. Si us plau, torna-ho a intentar fent clic al següent botó.",
  FLYWIRE_PAYMENT_INSTALMENT_DETAIL:
    "Fes clic per realitzar el pagament de la quota actual i recorda que cada mes rebràs un correu electrònic per poder realitzar el pagament de la quota corresponent a través del mètode de pagament que més et convingui.",
  PAYMENT_PLAN_LINE_PENDING_TITLE: "Quota no pagada",
  PAYMENT_PLAN_LINE_PENDING_TEXT:
    "Ha sorgit un problema en realitzar el pagament d'aquesta quota. Si us plau, torna a intentar fer el pagament fent clic al botó.",
  PAYMENT_RESULT_PAID_TITLE: "Pagament realitzat correctament",
  PAYMENT_RESULT_PENDING_TITLE: "Ooops!",
  PAYMENT_RESULT_PENDING_TEXT:
    "Sembla que s'ha produït un error en intentar processar el pagament de la quota. Si us plau, fes clic al botó per reintentar el pagament de la quota.",
  QUOTA_WITH_NUMBER: "Quota $v1",
  QUOTA_AND_SETUP_FEE_WITH_NUMBER: "Quota $v1 + Comissió d'obertura",
  PAY_WITH_FLYWIRE: "Paga amb Flywire",
  EMAIL_SHOULD_NOT_BE_THE_SAME_AS:
    "L'adreça electrònica no pot ser la mateixa que la de $v1",
  PHONE_NUMBER_SHOULD_NOT_BE_THE_SAME_AS:
    "El número de telèfon no pot ser el mateix que el de $v1",
  SHOW_MORE: "Mostra més",
  SHOW_LESS: "Amaga",
  PAYMENT_PLAN_LINE_PROCESSING_TITLE: "Pagament iniciat",
  PAYMENT_PLAN_LINE_PROCESSING_TEXT:
    "El pagament de la quota s'ha iniciat i es troba en estat pendent. Si has rebut instruccions per fer el pagament, si us plau, completa-les i torna a accedir a aquest enllaç. Si no és així, pot ser que el sistema trigui una mica més del que és habitual. De tota manera, un cop el pagament hagi estat processat correctament, la quota apareixerà com a pagada. Si hi ha alguna incidència, podràs tornar a intentar el pagament.",
  PAYMENT_RESULT_PROCESSING_TITLE: "Pagament iniciat",
  PAYMENT_RESULT_PROCESSING_TEXT:
    "El pagament de la quota s'ha iniciat i es troba en estat pendent. Si has rebut instruccions per fer el pagament, si us plau, completa-les i torna a accedir a aquest enllaç. Si no és així, pot ser que el sistema trigui una mica més del que és habitual. De tota manera, un cop el pagament hagi estat processat correctament, la quota apareixerà com a pagada. Si hi ha alguna incidència, podràs tornar a intentar el pagament.",

  //ARIA LABELS
  DNI_ARIA_LABEL: "Document d'identitat espanyol",
  NIE_UE_ARIA_LABEL: "Document d'identitat europeu",
  NIE_NON_UE_ARIA_LABEL: "Document d'identitat fora de la Unió Europea",
  PASSPORT_ARIA_LABEL: "Passaport",

  //LENDER SELECTION
  INSTALMENT_AMOUNT: "Quota",
  SELECT: "Seleccionar",
  SELECT_LENDER: "Entitats financeres",
  SELECT_LENDER_SUBTITLE:
    "Selecciona amb quina entitat financera vols continuar el procés",
  SELECT_ONE_OF_THE_FOLLOWING_OPTIONS:
    "Selecciona una de les següents opcions:",
  SELECT_LENDER_DESCRIPTION:
    "Al seleccionar la entitat financera amb la qual vols continuar el procés, s'aplicaran les condicions que pots veure en la targeta de cada una de les opcions a la teva proposta de finançament.",
  ANSWERED_YES: "Acceptada",
  ANSWERED_NO: "Denegada",
  IN_STUDY: "En estudi",
  FINANCING_PROVIDED_BY: "Financiació oferta per",
  TO_PAY: "A pagar",
  CONTINUE_WITH: "Continuar amb",
  [LenderName.CAIXABANK]: "CaixaBank",
  [LenderName.CETELEM]: "Cetelem",
  [LenderName.BS]: "Banc Sabadell",
  [LenderName.BBVA]: "BBVA",
  [LenderName.SOYOU]: "Sofinco",
  [LenderName.ABANCA]: "Abanca",
  [LenderName.SANTANDER]: "Santander",
  [LenderName.SANTANDER_CONSUMER]: "Santander Consumer",

  LOAN_CONDITIONS: "Condicions",
  SECTION_LOAN_CONDITIONS: "Condicions",
  END_SECTION_LOAN_CONDITIONS: "Condicions seleccionades",
  AVAILABLE_LENDERS: "Entitats disponibles en el termini seleccionat",
  LENDERS: "Entitats disponibles",
  QUOTA_SELECTION: "Selecció de quota",
  SECTION_LOAN_CONDITIONS_END: "Condicions seleccionades",

  //Call Me back panel
  CAN_WE_HELP_YOU: "Parlem?",
  FULL_NAME: "Nom i cognoms",
  PREFERRED_TIME_SLOT: "Hora preferida",
  DO_YOU_NEED_HELP_WITH_YOUR_APPLICATION:
    "Necessites ajuda amb la teva sol·licitud?",
  YOU_CAN_CALL_US_OR_WE_CALL_YOU_BACK:
    "Pots trucar-nos al 931 227 103 o dejanos els teus dades i et trucarem nosaltres.",
  REQUEST_CALL: "Sol·licitar trucada",
  PHONE_NUMBER: "Telèfon de contacte",
  ASAP: "Tan aviat com sigui possible",
  MORNING: "Matí (9h-12h)",
  NOON: "Migdia (12h-15h)",
  AFTERNOON: "Tarda (15h-18h)",
  EVENING: "Nit (18h-21h)",
  THANK_YOU_FOR_COMMUNICATING_WITH_US:
    "Gràcies per comunicar-te amb nosaltres!",
  WE_WILL_CALL_YOU_BACK_AT_THE_SPECIFIED_TIME:
    "Et trucarem a la franja indicada.",
  CALL_ME_BACK_DATA_TREATMENT: (
    <>
      <h3>Informació sobre el tractament de dades:</h3>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Finalitat:</strong> Gestionar la teva sol·licitud d’assistència
        en el finançament i contactar-te telefònicament segons ens has
        sol·licitat.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Base jurídica:</strong> Aplicació de mesures precontractuals
        (art. 6.1.b RGPD).
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Conservació:</strong> Conservarem les teves dades mentre
        gestionem la teva sol·licitud i fins a 15 dies després de l’última
        interacció, bloquejant-les posteriorment per atendre possibles
        reclamacions d’acord amb la normativa.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        <strong>Drets:</strong> Pots exercir els teus drets d’accés,
        rectificació, supressió, oposició, limitació i portabilitat a través de{" "}
        <a href="mailto:ejerciciodederechos@caixabankpc.com" target="_blank">
          ejerciciodederechos@caixabankpc.com
        </a>{" "}
        indicant com a referència “Facilitea – Vehicle d’Ocasió” i aportant una
        còpia del teu document d’identitat.
      </p>
      <p style={{ marginBlock: "0.3rem" }}>
        Pots consultar la informació completa sobre el tractament de les teves
        dades a la nostra política de privacitat{" "}
        <a
          href="https://www.caixabankpc.com/es/politica-de-privacidad"
          target="_blank"
        >
          https://www.caixabankpc.com/es/politica-de-privacidad
        </a>
      </p>
    </>
  ),
};

export default ca;
